import { useEffect, useState, useRef, useCallback } from "react";
import { useAction, useMutation } from "convex/react";
import { useWindowContext } from "../desktop/WindowContext";
import { TreePane } from "./TreePane";
import { DocumentList } from "./DocumentList";
import { DocumentPreview } from "./DocumentPreview";
import { ImportWizard, type ImportSelection } from "./ImportWizard";
import { BulkImportWizard } from "./BulkImportWizard";
import { ImportJobProgressWindow } from "./ImportJobProgressWindow";
import { testIds } from "../../lib/testIds";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { driveItemToInput } from "@repancy/graph-core";
import {
  isBgImportEnabled,
  makeBgImportIdempotencyKey,
} from "../../lib/azure/bgImportFlag";
import type { PreviewSource } from "../../lib/sharepoint/previewSource";
import { HelpTip } from "../ui/HelpTip";
import type { HelpCopyKey } from "../../lib/help/copy";

const CLIENT_NAMES: Record<string, string> = {
  c1: "Johnson Industries",
  c2: "Rivera Family Trust",
  c3: "TechStart Inc.",
  c4: "Greenfield HOA",
  c5: "Pacific Ventures",
};

const MATTER_INFO: Record<string, { title: string; clientId: string }> = {
  m1: { title: "Asset Purchase Agreement", clientId: "c1" },
  m2: { title: "Employment Restructuring", clientId: "c1" },
  m3: { title: "Trust Amendment", clientId: "c2" },
  m4: { title: "Beneficiary Dispute", clientId: "c2" },
  m5: { title: "Series B Financing", clientId: "c3" },
  m6: { title: "IP Licensing Agreement", clientId: "c3" },
  m7: { title: "CC&R Amendment", clientId: "c4" },
  m8: { title: "Construction Dispute", clientId: "c4" },
  m9: { title: "Fund Formation", clientId: "c5" },
  m10: { title: "Portfolio Company Acquisition", clientId: "c5" },
};

const MIN_TREE_W = 150;
const MAX_TREE_W = 500;
const MIN_PREVIEW_H = 80;
const MAX_PREVIEW_H = 500;

/**
 * XP File Explorer toolbar button — flat by default, raised on hover,
 * pressed on active. Mirrors `explorer.exe` toolbar behavior.
 */
function ToolbarButton({
  label,
  icon,
  onClick,
  disabled,
  tooltip,
  helpKey,
  "data-testid": dataTestId,
}: {
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
  /**
   * Stable key into the centralized helpCopy registry. Preferred over
   * `tooltip` (a raw string) because the registry entry is type-safe,
   * verified against component behavior by `copyTruth.test.ts`, and
   * picks up `long` "Learn more" bodies automatically.
   */
  helpKey?: HelpCopyKey;
  "data-testid"?: string;
}) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const button = (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={dataTestId}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setActive(false);
      }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      className="flex items-center gap-1 select-none"
      style={{
        height: 28,
        padding: "0 8px",
        background: hover && !disabled ? "#fff" : "transparent",
        border:
          hover && !disabled
            ? active
              ? "1px solid #316ac5"
              : "1px solid #555555"
            : "1px solid transparent",
        boxShadow: active && !disabled ? "inset 1px 1px 1px rgba(0,0,0,0.15)" : "none",
        fontFamily: "var(--xp-font-body)",
        fontSize: 11,
        cursor: disabled ? "default" : "pointer",
        color: disabled ? "#a0a0a0" : "#000",
        whiteSpace: "nowrap",
        flexShrink: 0,
        borderRadius: 2,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {icon ? (
        <img
          src={icon}
          alt=""
          draggable={false}
          style={{ width: 22, height: 22, imageRendering: "pixelated" }}
        />
      ) : null}
      <span>{label}</span>
    </button>
  );
  if (helpKey !== undefined) {
    return (
      <HelpTip k={helpKey} wrap>
        {button}
      </HelpTip>
    );
  }
  // Ad-hoc fallback for the long tail of toolbar buttons not yet in the
  // helpCopy registry (Search/Folders/Import-from-SharePoint…). Same
  // bubble visual as the keyed path; just bypasses the registry.
  return (
    <HelpTip short={tooltip ?? label} wrap>
      {button}
    </HelpTip>
  );
}

/**
 * Common Tasks side panel — the blue-gradient task pane on the left of
 * a real XP File Explorer. Collapsible; the explorer body keeps the
 * tree-pane to the right of it. Optional in narrow viewports.
 */
// Disabled-button no-op — TaskLink/ToolbarButton both gate onClick on
// `disabled` so this is never called. Existed because onClick is a
// required prop; named explicitly so smoke source-greps can distinguish
// it from the bare `() => {}` "dead handler" pattern.
const DISABLED_NOOP = () => {};

function CommonTasksPane({
  selectedMatterId,
  onImportSharePoint,
  onImportNotesFolder,
  onGoToClientsRoot,
}: {
  selectedMatterId: string | null;
  onImportSharePoint: () => void;
  onImportNotesFolder: () => void;
  onGoToClientsRoot: () => void;
}) {
  return (
    <div
      data-testid={testIds.explorer.commonTasks}
      className="flex flex-col gap-2 flex-shrink-0"
      style={{
        width: 180,
        padding: 8,
        background: "linear-gradient(180deg, #7da2ce 0%, #4f81bd 50%, #2a5286 100%)",
        color: "#fff",
        fontFamily: "var(--xp-font-body)",
        fontSize: 11,
        overflowY: "auto",
      }}
    >
      <TaskGroup title="File and Folder Tasks">
        <TaskLink
          label="Import from SharePoint…"
          icon="/xp/icons/icon__share_folder--large.png"
          onClick={onImportSharePoint}
          disabled={!selectedMatterId}
          testId="explorer-import-sharepoint-task"
        />
        {/* Import notes folder — opens the Obsidian Import wizard
            inline. Folded in here so users have one place to start
            an import. */}
        <TaskLink
          label="Import notes folder…"
          icon="/xp/icons/icon__share_folder--large.png"
          onClick={onImportNotesFolder}
          testId="explorer-import-notes-folder-task"
        />
        {/* "New folder" — vault writes go through SaveDialog from the
            Save Output / NIPE pending-writes tray surfaces, not from
            this pane. Kept disabled per phase-J dead-button sweep. */}
        <TaskLink
          label="New folder"
          icon="/xp/icons/icon__new_folder--large.png"
          onClick={DISABLED_NOOP}
          disabled
        />
      </TaskGroup>
      <TaskGroup title="Other Places">
        {/* "Clients" — jump to the clients-root view (clear selected
            matter + document). Wired in the phase-J dead-button sweep. */}
        <TaskLink
          label="Clients"
          icon="/xp/icons/icon__network_places--large.png"
          onClick={onGoToClientsRoot}
          testId="explorer-task-clients-root"
        />
        {/* "My Documents" — the Explorer's data model is rooted at
            Clients > Matter; there's no per-user "My Documents" surface.
            Kept disabled per phase-J dead-button sweep. */}
        <TaskLink
          label="My Documents"
          icon="/xp/icons/icon__documents--large.png"
          onClick={DISABLED_NOOP}
          disabled
        />
      </TaskGroup>
    </div>
  );
}

function TaskGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      className="flex flex-col gap-1"
      style={{
        background: "rgba(255,255,255,0.92)",
        color: "#1a3a6a",
        padding: 6,
        borderRadius: 4,
        boxShadow: "0 1px 0 rgba(0,0,0,0.2)",
      }}
    >
      <div
        style={{
          fontWeight: 700,
          fontSize: 12,
          color: "#1a3a6a",
          borderBottom: "1px solid #b8c8e0",
          paddingBottom: 2,
          marginBottom: 2,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function TaskLink({
  label,
  icon,
  onClick,
  disabled,
  testId,
}: {
  label: string;
  icon: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testId}
      className="flex items-center gap-1.5 text-left select-none"
      style={{
        background: "transparent",
        border: "none",
        color: disabled ? "#7a8aa0" : "#1a3a6a",
        cursor: disabled ? "default" : "pointer",
        fontFamily: "var(--xp-font-body)",
        fontSize: 11,
        padding: "1px 0",
        textDecoration: "none",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.textDecoration = "underline";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.textDecoration = "none";
      }}
    >
      <img
        src={icon}
        alt=""
        draggable={false}
        style={{ width: 18, height: 18, imageRendering: "pixelated", flexShrink: 0 }}
      />
      <span>{label}</span>
    </button>
  );
}

export function ExplorerWindow() {
  const { openWindow } = useWindowContext();
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  // K.8 — preview source. When non-null, drives the DocumentPreview pane.
  // For matter docs this mirrors `selectedDocumentId` via {kind:'convex',id};
  // for K.7 connected-site driveItems it carries the siteId+driveId+itemId
  // tuple so the preview pane can fetch via the Node action.
  const [previewSource, setPreviewSource] = useState<PreviewSource | null>(null);
  const [treeWidth, setTreeWidth] = useState(250);
  const [previewHeight, setPreviewHeight] = useState(200);
  const [navHistory, setNavHistory] = useState<string[]>([]);
  const [navIndex, setNavIndex] = useState(-1);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardProgress, setWizardProgress] = useState<{ completed: number; total: number; errors: number } | undefined>(undefined);
  // F-BULK-IMPORT — bulk wizard + progress drawer state. Separate
  // from the single-pick wizard above; mounted inline at the bottom
  // of the explorer surface alongside ImportWizard.
  const [bulkWizardOpen, setBulkWizardOpen] = useState(false);
  const [bulkProgressOpen, setBulkProgressOpen] = useState(false);
  const [bulkFocusJobId, setBulkFocusJobId] = useState<
    Id<'importJobs'> | undefined
  >(undefined);
  const upsertFromExternal = useMutation(api.documents.upsertFromExternal);
  const auditAppend = useMutation(api.sharepointAudit.append);
  const enqueueBgImport = useAction(api.azureBridge.enqueueImport);

  const draggingV = useRef(false);
  const dragStartV = useRef({ x: 0, w: 0 });
  const draggingH = useRef(false);
  const dragStartH = useRef({ y: 0, h: 0 });

  // F-NIPE-DESKTOP-AWARENESS — read-only bridge for
  // readWindowContext('/documents'). Maps the explorer's preview-
  // source kind to the tree-group it lives in. Demo vs portal split
  // isn't visible here; we report both as 'portal' and the model can
  // disambiguate from the [window-context] block's mode line.
  const docLibBridgeRef = useRef({
    selectedMatterId,
    selectedDocumentId,
    previewSource,
  });
  docLibBridgeRef.current = {
    selectedMatterId,
    selectedDocumentId,
    previewSource,
  };
  useEffect(() => {
    window.__repancyDocumentLibrary = () => {
      const src = docLibBridgeRef.current.previewSource;
      let group: 'portal' | 'demo' | 'connected' | null = null;
      let docName: string | null = null;
      if (src) {
        if (src.kind === 'remote-driveItem') {
          group = 'connected';
          docName = src.name;
        } else if (src.kind === 'remote-folder') {
          group = 'connected';
          docName = src.name;
        } else if (src.kind === 'convex') {
          group = 'portal';
        }
      }
      return {
        selectedDocumentId: docLibBridgeRef.current.selectedDocumentId,
        selectedDocumentName: docName,
        currentTreeGroup: group,
        currentMatterPath: docLibBridgeRef.current.selectedMatterId,
      };
    };
    return () => {
      delete window.__repancyDocumentLibrary;
    };
  }, []);

  const handleSelectMatter = useCallback((matterId: string) => {
    setSelectedMatterId(matterId);
    setSelectedDocumentId(null);
    setPreviewSource(null);
    setNavHistory((prev) => {
      const next = prev.slice(0, navIndex + 1);
      next.push(matterId);
      return next;
    });
    setNavIndex((prev) => prev + 1);
  }, [navIndex]);

  const handleSelectDocument = useCallback((docId: string) => {
    setSelectedDocumentId(docId);
    setPreviewSource({ kind: 'convex', id: docId });
  }, []);

  // K.8 — connected-site driveItem selected from the tree. The remote
  // preview source carries the Graph addressing tuple; DocumentPreview
  // fetches via the Node action.
  const handleSelectRemoteDriveItem = useCallback(
    (item: {
      siteId: string;
      driveId: string;
      itemId: string;
      name: string;
      webUrl: string | null;
    }) => {
      setSelectedDocumentId(null);
      setPreviewSource({ kind: 'remote-driveItem', ...item });
    },
    [],
  );

  // K.9.8 — connected-site folder selected from the tree (folder ROW
  // click, NOT the ▶ expand toggle). RemoteFolderPreview shows name +
  // child counts + Open-in-SharePoint link.
  const handleSelectRemoteFolder = useCallback(
    (item: {
      siteId: string;
      driveId: string;
      folderId: string;
      name: string;
      webUrl: string | null;
    }) => {
      setSelectedDocumentId(null);
      setPreviewSource({ kind: 'remote-folder', ...item });
    },
    [],
  );

  const canGoBack = navIndex > 0;
  const canGoForward = navIndex < navHistory.length - 1;

  function goBack() {
    if (!canGoBack) return;
    const newIdx = navIndex - 1;
    setNavIndex(newIdx);
    setSelectedMatterId(navHistory[newIdx] ?? null);
    setSelectedDocumentId(null);
    setPreviewSource(null);
  }

  function goForward() {
    if (!canGoForward) return;
    const newIdx = navIndex + 1;
    setNavIndex(newIdx);
    setSelectedMatterId(navHistory[newIdx] ?? null);
    setSelectedDocumentId(null);
    setPreviewSource(null);
  }

  function goUp() {
    if (previewSource !== null) {
      setPreviewSource(null);
      setSelectedDocumentId(null);
    } else if (selectedMatterId) {
      setSelectedMatterId(null);
    }
  }

  // Phase-J dead-button sweep — CommonTasksPane "Clients" link jumps
  // back to the clients-root view (the same place goUp() lands when
  // a matter is selected). Pushes the root onto nav history so Back
  // still works.
  const handleGoToClientsRoot = useCallback(() => {
    setSelectedMatterId(null);
    setSelectedDocumentId(null);
    setNavHistory((prev) => {
      const next = prev.slice(0, navIndex + 1);
      next.push('');
      return next;
    });
    setNavIndex((prev) => prev + 1);
  }, [navIndex]);

  function buildPath(): string {
    const parts = ["Clients"];
    if (selectedMatterId) {
      const mInfo = MATTER_INFO[selectedMatterId];
      if (mInfo) {
        const clientName = CLIENT_NAMES[mInfo.clientId];
        if (clientName) parts.push(clientName);
        parts.push(mInfo.title);
      }
    }
    return parts.join(" > ");
  }

  function handleVDividerMouseDown(e: React.MouseEvent) {
    e.preventDefault();
    draggingV.current = true;
    dragStartV.current = { x: e.clientX, w: treeWidth };
    function onMouseMove(ev: MouseEvent) {
      if (!draggingV.current) return;
      const newW = dragStartV.current.w + (ev.clientX - dragStartV.current.x);
      setTreeWidth(Math.max(MIN_TREE_W, Math.min(MAX_TREE_W, newW)));
    }
    function onMouseUp() {
      draggingV.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  function handleHDividerMouseDown(e: React.MouseEvent) {
    e.preventDefault();
    draggingH.current = true;
    dragStartH.current = { y: e.clientY, h: previewHeight };
    function onMouseMove(ev: MouseEvent) {
      if (!draggingH.current) return;
      const newH = dragStartH.current.h - (ev.clientY - dragStartH.current.y);
      setPreviewHeight(Math.max(MIN_PREVIEW_H, Math.min(MAX_PREVIEW_H, newH)));
    }
    function onMouseUp() {
      draggingH.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  return (
    <div
      data-testid={testIds.explorer.root}
      className="flex flex-col w-full h-full overflow-hidden"
      style={{
        fontFamily: "var(--xp-font-body)",
        fontSize: 12,
        background: "#ece9d8",
      }}
    >
      {/* Toolbar */}
      <div
        data-testid={testIds.explorer.toolbar}
        className="flex items-center flex-shrink-0"
        style={{
          gap: 2,
          padding: "2px 6px",
          background: "linear-gradient(180deg, #f5f4ea 0%, #ece9d8 100%)",
          borderBottom: "1px solid #aca899",
        }}
      >
        <ToolbarButton
          label="Back"
          icon="/xp/icons/icon__back.png"
          helpKey="explorer.toolbar.back"
          onClick={goBack}
          disabled={!canGoBack}
          data-testid={testIds.explorer.backButton}
        />
        <ToolbarButton
          label="Forward"
          icon="/xp/icons/icon__forward.png"
          helpKey="explorer.toolbar.forward"
          onClick={goForward}
          disabled={!canGoForward}
          data-testid={testIds.explorer.forwardButton}
        />
        <ToolbarButton
          label="Up"
          icon="/xp/icons/icon__up.png"
          helpKey="explorer.toolbar.up"
          onClick={goUp}
          data-testid={testIds.explorer.upButton}
        />
        <div style={{ width: 1, height: 22, background: "#aca899", margin: "0 4px", flexShrink: 0 }} />
        {/* "Search" / "Folders" — XP-style placeholders kept disabled.
            Corpus Search lives in its own /corpus-search window; the
            tree pane is already visible via the left split. Kept
            disabled per phase-J dead-button sweep — see decisions.md. */}
        <ToolbarButton
          label="Search"
          icon="/xp/icons/icon__search--large.png"
          onClick={DISABLED_NOOP}
          disabled
        />
        <ToolbarButton
          label="Folders"
          icon="/xp/icons/icon__folders.png"
          onClick={DISABLED_NOOP}
          disabled
        />
        <div style={{ flex: 1 }} />
        <ToolbarButton
          label="Import from SharePoint…"
          icon="/xp/icons/icon__share_folder--large.png"
          helpKey="explorer.toolbar.importFromSharePoint"
          onClick={() => setWizardOpen(true)}
          disabled={!selectedMatterId}
          data-testid="explorer-import-sharepoint"
        />
        <ToolbarButton
          label="Bulk import…"
          icon="/xp/icons/icon__share_folder--large.png"
          helpKey="explorer.toolbar.bulkImport"
          onClick={() => setBulkWizardOpen(true)}
          disabled={!selectedMatterId}
          data-testid="explorer-bulk-import"
        />
        <ToolbarButton
          label="Imports running"
          icon="/xp/icons/icon__share_folder--large.png"
          helpKey="explorer.toolbar.importsRunning"
          onClick={() => setBulkProgressOpen(true)}
          data-testid="explorer-open-bulk-progress"
        />
      </div>

      {/* Address bar */}
      <div
        data-testid={testIds.explorer.addressBar}
        className="flex items-center flex-shrink-0"
        style={{
          gap: 6,
          padding: "3px 6px",
          background: "#ece9d8",
          borderBottom: "1px solid #aca899",
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "#000",
            flexShrink: 0,
            fontFamily: "var(--xp-font-body)",
          }}
        >
          Address
        </span>
        <div
          className="flex items-center flex-1 overflow-hidden"
          style={{
            height: 22,
            padding: "0 6px",
            background: "#fff",
            border: "1px solid #7f9db9",
            fontSize: 12,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          <img
            src="/xp/icons/icon__file_explorer.png"
            alt=""
            draggable={false}
            style={{ width: 16, height: 16, imageRendering: "pixelated", marginRight: 4, flexShrink: 0 }}
          />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{buildPath()}</span>
        </div>
        {/* Address-bar "Go" — disabled placeholder. The address bar
            is read-only; navigation happens via TreePane + Back/Forward/Up.
            Kept disabled per phase-J dead-button sweep. */}
        <button
          type="button"
          onClick={DISABLED_NOOP}
          disabled
          className="flex items-center gap-1 select-none"
          style={{
            height: 22,
            padding: "0 8px",
            background: "linear-gradient(180deg, #f5f4ea 0%, #ece9d8 100%)",
            border: "1px solid #aca899",
            fontFamily: "var(--xp-font-body)",
            fontSize: 11,
            color: "#000",
            cursor: "default",
          }}
        >
          <img
            src="/xp/icons/icon__go.png"
            alt=""
            draggable={false}
            style={{ width: 16, height: 16, imageRendering: "pixelated" }}
          />
          Go
        </button>
      </div>

      {/* Main content area — Common Tasks pane + tree + list + preview */}
      <div className="flex flex-1 overflow-hidden">
        <CommonTasksPane
          selectedMatterId={selectedMatterId}
          onImportSharePoint={() => setWizardOpen(true)}
          onImportNotesFolder={() => openWindow("/obsidian-import")}
          onGoToClientsRoot={handleGoToClientsRoot}
        />
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex flex-1 overflow-hidden">
            <div
              data-testid={testIds.explorer.treePane}
              className="flex-shrink-0 overflow-hidden"
              style={{ width: treeWidth, background: "#fff" }}
            >
              <TreePane
                onSelectMatter={handleSelectMatter}
                onSelectDocument={handleSelectDocument}
                onSelectRemoteDriveItem={handleSelectRemoteDriveItem}
                onSelectRemoteFolder={handleSelectRemoteFolder}
                selectedMatterId={selectedMatterId}
              />
            </div>
            <div
              onMouseDown={handleVDividerMouseDown}
              data-testid={testIds.explorer.treeDivider}
              className="flex-shrink-0"
              style={{
                width: 5,
                cursor: "col-resize",
                background: "#ece9d8",
                borderLeft: "1px solid #aca899",
                borderRight: "1px solid #fff",
              }}
            />
            <div
              data-testid={testIds.explorer.listPane}
              className="flex-1 overflow-hidden"
              style={{ background: "#fff" }}
            >
              <DocumentList
                matterId={selectedMatterId}
                onSelectDocument={handleSelectDocument}
                selectedDocumentId={selectedDocumentId}
              />
            </div>
          </div>
          <div
            onMouseDown={handleHDividerMouseDown}
            data-testid={testIds.explorer.previewDivider}
            className="flex-shrink-0"
            style={{
              height: 5,
              cursor: "row-resize",
              background: "#ece9d8",
              borderTop: "1px solid #aca899",
              borderBottom: "1px solid #fff",
            }}
          />
          <div
            data-testid={testIds.explorer.previewPane}
            className="flex-shrink-0 overflow-hidden"
            style={{ height: previewHeight, background: "#fff" }}
          >
            <DocumentPreview
              documentId={selectedDocumentId}
              previewSource={previewSource}
            />
          </div>
        </div>
      </div>

      <ImportWizard
        open={wizardOpen}
        onCancel={() => { setWizardOpen(false); setWizardProgress(undefined); }}
        progress={wizardProgress}
        onImport={async (selection: ImportSelection) => {
          if (!selectedMatterId) {
            throw new Error('No matter selected');
          }
          if (isBgImportEnabled()) {
            const idempotencyKey = makeBgImportIdempotencyKey(
              selection.driveId,
              selectedMatterId,
            );
            try {
              const result = await enqueueBgImport({
                matterId: selectedMatterId as Id<'matters'>,
                siteId: selection.siteId,
                driveId: selection.driveId,
                linkMode: selection.linkMode,
                idempotencyKey,
              });
              setWizardProgress({
                completed: result.deduped ? 1 : 0,
                total: 1,
                errors: 0,
              });
              console.info('[bg-import] queued', result);
              return;
            } catch (err) {
              console.error('[bg-import] failed', err);
              setWizardProgress({ completed: 0, total: 1, errors: 1 });
              throw err;
            }
          }
          const total = selection.items.length;
          let completed = 0;
          let errors = 0;
          setWizardProgress({ completed, total, errors });
          for (const item of selection.items) {
            try {
              const mapped = driveItemToInput(item, selection.siteId);
              const documentId = await upsertFromExternal({
                matterId: selectedMatterId as Id<'matters'>,
                title: mapped.title,
                content: '',
                documentType: mapped.documentType,
                confidentialityLevel: mapped.confidentialityLevel,
                externalRef: mapped.externalRef,
                copyOnly: selection.linkMode === 'copy',
              });
              await auditAppend({
                op: 'import',
                itemId: mapped.externalRef.itemId,
                driveId: mapped.externalRef.driveId,
                siteId: mapped.externalRef.siteId,
                etagAfter: mapped.externalRef.etag,
                documentId,
                note: selection.linkMode === 'copy' ? 'one-shot copy' : 'linked import',
              });
              completed += 1;
            } catch (err) {
              errors += 1;
              console.warn('Import failed for item', item.id, err);
            }
            setWizardProgress({ completed, total, errors });
          }
        }}
      />

      <BulkImportWizard
        open={bulkWizardOpen}
        onCancel={() => setBulkWizardOpen(false)}
        defaultMatterId={selectedMatterId as Id<'matters'> | undefined}
        onLaunched={(jobId) => {
          setBulkFocusJobId(jobId);
          setBulkProgressOpen(true);
        }}
      />

      {bulkProgressOpen && (
        <div
          style={{
            position: 'fixed',
            top: 80,
            right: 40,
            width: 520,
            height: 480,
            zIndex: 9999,
            background: '#ece9d8',
            borderStyle: 'solid',
            borderWidth: 2,
            boxShadow: '3px 3px 0 #000',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              background: 'linear-gradient(90deg, var(--xp-selection), #1a4ab8)',
              color: 'white',
              padding: '3px 6px',
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>Bulk imports</span>
            <button
              onClick={() => setBulkProgressOpen(false)}
              style={{
                background: '#ece9d8',
                color: '#000',
                border: '1px solid #000',
                cursor: 'pointer',
                padding: '0 6px',
                fontSize: 11,
              }}
              data-testid="explorer-close-bulk-progress"
            >
              ✕
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <ImportJobProgressWindow focusJobId={bulkFocusJobId} />
          </div>
        </div>
      )}
    </div>
  );
}
